import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2, ChevronLeft, ChevronRight, ScrollText, Search } from 'lucide-react';
import { useAuditLogsQuery, useAuditFiltersQuery } from '../services/hooks/useAuditLogs';
import { AuditLogQuery } from '../services/api/auditLogService';

// Human-readable Spanish labels for action codes + target types.
const ACTION_LABELS: Record<string, string> = {
    'class.cancel': 'Clase cancelada',
    'class.restore': 'Clase reestablecida',
    'event.create': 'Clase creada',
    'event.update': 'Clase editada',
    'event.update_future': 'Clase y futuras editadas',
    'event.enrollment_change': 'Clientes de la clase modificados',
    'event.bulk_create': 'Horario generado',
    'event.bulk_delete': 'Eventos eliminados (masivo)',
    'event.delete': 'Evento eliminado',
    // D2 — personas de prueba
    'event.prospect_add': 'Persona de prueba apuntada a la clase',
    'event.prospect_remove': 'Persona de prueba retirada de la clase',
    'schedule.assign_clients': 'Asignación masiva de clientes',
    'plaza.create': 'Plaza creada',
    'plaza.move': 'Plaza movida',
    'plaza.remove': 'Plaza eliminada',
    'client.create': 'Cliente creado',
    'client.deactivate': 'Cliente dado de baja',
    'client.restore': 'Cliente restaurado',
    // B5 — cambio de correo sincronizado (o no) con el acceso al portal.
    'client.email_change': 'Correo del cliente cambiado',
    'prospect.create': 'Persona de prueba registrada',
    'prospect.update': 'Persona de prueba editada',
    'prospect.deactivate': 'Persona de prueba dada de baja',
    'prospect.convert': 'Persona de prueba convertida en cliente',
    'config.create': 'Configuración creada',
    'config.update': 'Configuración modificada',
    'config.delete': 'Configuración eliminada',
    'classroom.create': 'Sala creada',
    'classroom.update': 'Sala modificada',
    'classroom.delete': 'Sala eliminada',
    // B1 — sin estas etiquetas la pantalla mostraba los códigos en crudo.
    'classroom.archive': 'Sala archivada',
    'classroom.reassign': 'Clases de la sala reasignadas',
    'nivel.create': 'Nivel creado',
    'nivel.update': 'Nivel modificado',
    'nivel.delete': 'Nivel eliminado',
    'nivel.archive': 'Nivel archivado',
    'nivel.reassign': 'Clases del nivel reasignadas',
    'instructor.create': 'Instructor creado',
    'instructor.update': 'Instructor modificado',
    'instructor.delete': 'Instructor eliminado',
    'instructor.archive': 'Instructor archivado',
    'instructor.reassign': 'Clases del instructor reasignadas',
    'user.create': 'Usuario creado',
    'user.update_role': 'Rol de usuario cambiado',
    'user.revoke_access': 'Usuario dado de baja',
    'user.restore_access': 'Acceso de usuario restaurado',
    'user.invitation_revoke': 'Invitación cancelada',
    'recovery.grant': 'Recuperación otorgada',
    'recovery.revoke': 'Recuperación revocada',
};
const TARGET_LABELS: Record<string, string> = {
    evento: 'Evento', plaza: 'Plaza', client: 'Cliente', user: 'Usuario',
    schedule: 'Horario', configuration: 'Configuración',
    classroom: 'Sala', nivel: 'Nivel', instructor: 'Instructor', recovery: 'Recuperación',
};
const actionLabel = (a: string) => ACTION_LABELS[a] || a;
const targetLabel = (t: string | null) => (t ? TARGET_LABELS[t] || t : '—');

// Colour the action chip by domain.
const actionClass = (a: string) => {
    if (a.startsWith('class.') || a.startsWith('event.') || a.startsWith('schedule.')) return 'bg-purple-50 text-purple-700';
    if (a.startsWith('plaza.')) return 'bg-blue-50 text-blue-700';
    if (a.startsWith('prospect.') || a.includes('prospect_')) return 'bg-fuchsia-50 text-fuchsia-700';
    if (a.startsWith('client.')) return 'bg-green-50 text-green-700';
    if (a.startsWith('user.')) return 'bg-amber-50 text-amber-700';
    if (a.startsWith('config.')) return 'bg-rose-50 text-rose-700';
    if (a.startsWith('classroom.') || a.startsWith('nivel.') || a.startsWith('instructor.')) return 'bg-slate-100 text-slate-700';
    return 'bg-gray-100 text-gray-700';
};

const AuditLogPage = () => {
    const [filters, setFilters] = useState<AuditLogQuery>({ page: 1, limit: 50 });
    const [searchInput, setSearchInput] = useState('');

    const { data, isLoading, isFetching } = useAuditLogsQuery(filters);
    const { data: filterOptions } = useAuditFiltersQuery();

    const setFilter = (patch: Partial<AuditLogQuery>) =>
        setFilters((prev) => ({ ...prev, ...patch, page: 1 })); // any filter change resets to page 1

    const items = data?.items ?? [];
    const page = data?.page ?? 1;
    const pages = data?.pages ?? 1;
    const total = data?.total ?? 0;

    // Rango mostrado en esta página: "Mostrando 51–93 de 93".
    const rangeFrom = total === 0 ? 0 : (page - 1) * (data?.limit ?? 50) + 1;
    const rangeTo = total === 0 ? 0 : rangeFrom + items.length - 1;

    return (
        <div className="flex flex-col w-full min-h-screen">
            <main className="flex-1 overflow-y-auto px-4 pb-4 pt-20 w-full">
                <div className="max-w-6xl mx-auto pb-16">
                    <div className="flex items-center gap-2 mb-1">
                        <ScrollText size={22} />
                        <h1 className="text-2xl font-bold">Auditoría</h1>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                        Registro de acciones administrativas: quién hizo qué y cuándo.
                    </p>

                    {/* Filtros */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                        <select
                            value={filters.action ?? ''}
                            onChange={(e) => setFilter({ action: e.target.value || undefined })}
                            className="border rounded px-3 py-2 text-sm"
                        >
                            <option value="">Todas las acciones</option>
                            {/* Ordenado por la etiqueta visible, no por el código interno. */}
                            {[...(filterOptions?.actions ?? [])]
                                .sort((a, b) => actionLabel(a).localeCompare(actionLabel(b), 'es'))
                                .map((a) => (
                                    <option key={a} value={a}>{actionLabel(a)}</option>
                                ))}
                        </select>
                        <select
                            value={filters.targetType ?? ''}
                            onChange={(e) => setFilter({ targetType: e.target.value || undefined })}
                            className="border rounded px-3 py-2 text-sm"
                        >
                            <option value="">Todos los tipos</option>
                            {[...(filterOptions?.targetTypes ?? [])]
                                .sort((a, b) => targetLabel(a).localeCompare(targetLabel(b), 'es'))
                                .map((t) => (
                                    <option key={t} value={t}>{targetLabel(t)}</option>
                                ))}
                        </select>
                        <input
                            type="date"
                            value={filters.from ?? ''}
                            onChange={(e) => setFilter({ from: e.target.value || undefined })}
                            className="border rounded px-3 py-2 text-sm"
                            title="Desde"
                        />
                        <input
                            type="date"
                            value={filters.to ?? ''}
                            onChange={(e) => setFilter({ to: e.target.value || undefined })}
                            className="border rounded px-3 py-2 text-sm"
                            title="Hasta"
                        />
                        <form
                            onSubmit={(e) => { e.preventDefault(); setFilter({ q: searchInput || undefined }); }}
                            className="flex"
                        >
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Buscar objetivo…"
                                className="border rounded-l px-3 py-2 text-sm w-full"
                            />
                            <button type="submit" className="border border-l-0 rounded-r px-3 py-2 bg-gray-50 hover:bg-gray-100">
                                <Search size={16} />
                            </button>
                        </form>
                    </div>

                    {/* Tabla */}
                    <div className="border rounded-lg overflow-x-auto bg-white">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500 text-left">
                                <tr>
                                    <th className="px-4 py-2 font-medium whitespace-nowrap">Fecha</th>
                                    <th className="px-4 py-2 font-medium">Acción</th>
                                    <th className="px-4 py-2 font-medium">Objetivo</th>
                                    <th className="px-4 py-2 font-medium">Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={4} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline" /></td></tr>
                                ) : items.length === 0 ? (
                                    <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">No hay registros para estos filtros.</td></tr>
                                ) : (
                                    items.map((log) => (
                                        <tr key={log._id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                                                {format(new Date(log.createdAt), "d MMM yyyy, HH:mm", { locale: es })}
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${actionClass(log.action)}`}>
                                                    {actionLabel(log.action)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className="text-gray-400 text-xs">{targetLabel(log.targetType)}</span>
                                                {log.targetLabel && <div className="text-gray-700">{log.targetLabel}</div>}
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="text-gray-700">{log.actor?.email || '—'}</div>
                                                {log.actor?.role && <div className="text-xs text-gray-400">{log.actor.role}</div>}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                        <span>
                            Mostrando {rangeFrom}–{rangeTo} de {total} registro{total !== 1 ? 's' : ''}
                            {isFetching && <Loader2 className="animate-spin inline ml-2" size={14} />}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))}
                                disabled={page <= 1}
                                className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <span>Página {page} de {pages}</span>
                            <button
                                onClick={() => setFilters((p) => ({ ...p, page: Math.min(pages, (p.page ?? 1) + 1) }))}
                                disabled={page >= pages}
                                className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AuditLogPage;
